import axios from 'axios';
import { User, UserCreate, UserUpdate, UserAdminUpdate, Address, AddressCreate, AddressUpdate, UserStatus } from '../types/user';

// Get the API URL from environment variables or default if not set
const API_URL = process.env.REACT_APP_API_URL;

// Set up axios with base URL
// Export this instance so other modules can use it
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000'),
});

// Add auth token to requests if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Refresh token if 401 is received
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't tried to refresh the token yet
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          // No refresh token, logout
          authApi.logout();
          return Promise.reject(error);
        }
        
        // Try to refresh the token
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refresh_token: refreshToken
        });
        
        const { access_token, refresh_token } = response.data;
        
        // Store new tokens
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        
        // Retry the original request with the new token
        originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        // Failed to refresh token, logout
        authApi.logout();
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export const authApi = {
  // Register a new user
  register: async (userData: UserCreate): Promise<User> => {
    const response = await apiClient.post<User>('/auth/register', userData);
    return response.data;
  },
  
  // Login user
  login: async (email: string, password: string): Promise<{ access_token: string, refresh_token: string }> => {
    // Use URLSearchParams to send form data (OAuth2 standard)
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await axios.post(`${API_URL}/auth/token`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    
    // Store tokens in localStorage
    const { access_token, refresh_token } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    
    return response.data;
  },
  
  // Logout user
  logout: (): void => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  },
  
  // Get current user info
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/auth/me');
    // Store user data in localStorage
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  },
  
  // Change password for current user
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },
  
  // Check if user is logged in
  isLoggedIn: (): boolean => {
    return !!localStorage.getItem('access_token');
  },
  
  // Get stored user data
  getStoredUser: (): User | null => {
    const userData = localStorage.getItem('user');
    if (userData) {
      return JSON.parse(userData);
    }
    return null;
  },
  
  // Get token for a specific user (admin only)
  getUserToken: async (userId: number): Promise<{ access_token: string, refresh_token: string }> => {
    const response = await apiClient.get(`/auth/user-token/${userId}`);
    return response.data;
  },
  
  // Login as another user (admin only)
  loginAsUser: async (userId: number): Promise<User> => {
    // Get token for user
    const { access_token, refresh_token } = await authApi.getUserToken(userId);
    
    // Store tokens in localStorage
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    
    // Clear current user data to force reload
    localStorage.removeItem('user');
    
    // Get current user info with new token
    return authApi.getCurrentUser();
  }
};

export const userApi = {
  // Get all users (admin only)
  getUsers: async (skip: number = 0, limit: number = 100, status?: string, role?: string, search?: string): Promise<User[]> => {
    const params = { skip, limit, status, role, search };
    const response = await apiClient.get<User[]>('/users', { params });
    return response.data;
  },
  
  // Get a specific user (admin or self)
  getUser: async (userId: number): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${userId}`);
    return response.data;
  },
  
  // Update a user (admin or self)
  updateUser: async (userId: number, userData: UserUpdate): Promise<User> => {
    const response = await apiClient.put<User>(`/users/${userId}`, userData);
    return response.data;
  },
  
  // Admin update user (includes role and status)
  adminUpdateUser: async (userId: number, userData: UserAdminUpdate): Promise<User> => {
    const response = await apiClient.put<User>(`/users/admin/${userId}`, userData);
    return response.data;
  },
  
  // Approve a user (admin only)
  approveUser: async (userId: number): Promise<User> => {
    const response = await apiClient.put<User>(`/users/approve/${userId}`);
    return response.data;
  },
  
  // Block a user (admin only)
  blockUser: async (userId: number): Promise<User> => {
    const response = await apiClient.put<User>(`/users/block/${userId}`);
    return response.data;
  },
  
  // Unblock a user (admin only) - Use adminUpdateUser endpoint
  unblockUser: async (userId: number): Promise<User> => {
    const response = await apiClient.put<User>(`/users/admin/${userId}`, { status: UserStatus.APPROVED });
    return response.data;
  },
  
  // Reset a user's password (admin only)
  resetPassword: async (userId: number, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/users/reset-password/${userId}`, {
      new_password: newPassword
    });
    return response.data;
  }
};

export const addressApi = {
  // Get all addresses for current user
  getAddresses: async (): Promise<Address[]> => {
    try {
      const response = await apiClient.get<Address[]>('/addresses');
      return response.data;
    } catch (error) {
      console.error('Error fetching addresses:', error);
      throw error;
    }
  },
  
  // Create a new address
  createAddress: async (addressData: AddressCreate): Promise<Address> => {
    try {
      const response = await apiClient.post<Address>('/addresses', addressData);
      return response.data;
    } catch (error) {
      console.error('Error creating address:', error);
      throw error;
    }
  },
  
  // Get a specific address
  getAddress: async (addressId: number): Promise<Address> => {
    try {
      const response = await apiClient.get<Address>(`/addresses/${addressId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching address ${addressId}:`, error);
      throw error;
    }
  },
  
  // Update an address
  updateAddress: async (addressId: number, addressData: AddressUpdate): Promise<Address> => {
    try {
      const response = await apiClient.put<Address>(`/addresses/${addressId}`, addressData);
      return response.data;
    } catch (error) {
      console.error(`Error updating address ${addressId}:`, error);
      throw error;
    }
  },
  
  // Delete an address
  deleteAddress: async (addressId: number): Promise<void> => {
    try {
      await apiClient.delete(`/addresses/${addressId}`);
    } catch (error) {
      console.error(`Error deleting address ${addressId}:`, error);
      throw error;
    }
  }
};